/**
 * The tenant's geography (brief 2026-10-08 §8.4, R16, phase G1).
 *
 * Four levels: an ENTITY (a legal company), a DESTINATION (an operating
 * region whose code prefixes the log name: MY, SG, BN), a SECTOR (an
 * operational area inside a destination: KUL, PEN, LGK, MY-SG), and the guest
 * catalogue's CITIES & AREAS linked to a sector. Words on screen (D13):
 * "Destinations" = MY / SG / BN; "Cities & areas" = the catalogue's cities.
 *
 * `GET /api/geo` serves entities, destinations and sectors (with aliases) to
 * every signed-in user; `useGeo()` reads it. Every module that reads a code
 * should go through `makeGeo(snapshot).sector(code)`, which resolves aliases
 * (SIN -> SG, BKI -> BN). The logs path reads the running geography through
 * `opsGeo.ts` (G2): Logs pills and pages, imports, flight check, search,
 * weekly arrivals, the dashboard split.
 *
 * The vocabulary below (inks, detectors, rule fields and operators) is the
 * API's `GeoCatalog`; `GeoTests` fails when the two lists differ.
 */

export interface GeoEntityPublic {
  code: string;
  name: string;
  ref_label: string | null;
  currency: string | null;
  sort_order: number;
}

export interface GeoDestination {
  code: string;
  label: string;
  entity_code: string | null;
  countries: string[] | null;
  time_zone: string | null;
  phone_codes: string[] | null;
  ink: string | null;
  default_sector: string | null;
  guide_slug: string | null;
  show_in_logs: boolean | null;
  show_in_filters: boolean | null;
  sort_order: number | null;
  is_active: boolean | null;
}

export interface GeoSectorPublic {
  code: string;
  name: string;
  destination: string | null;
  aliases: string[];
  airports: string[];
  cross_border_to: string | null;
  ink: string | null;
  sort_order: number;
}

export interface GeoSnapshot {
  /** `tables`, or `builtin` before db/2026-10-15_geo_model.sql is applied. */
  source: 'tables' | 'builtin';
  entities: GeoEntityPublic[];
  destinations: GeoDestination[];
  sectors: GeoSectorPublic[];
}

// ── Vocabulary (the API's GeoCatalog) ──────────────────────────────────

export interface InkDef {
  key: string;
  label: string;
  /** The CSS value to paint with. */
  css: string;
}

/**
 * The inks a destination or sector may wear. The three `log-*` hues are the
 * Logs page's country colours (`logsCountry.ts`, defined under the CS/OPS
 * module); outside it they fall back to the nearest printed ink so a swatch
 * never paints blank. The rest are the skin's category inks.
 */
export const GEO_INKS: readonly InkDef[] = [
  { key: 'log-my', label: 'Logs orange', css: 'var(--log-my, var(--q-cat-orange))' },
  { key: 'log-sg', label: 'Logs blue', css: 'var(--log-sg, var(--q-cat-blue))' },
  { key: 'log-bn', label: 'Logs gold', css: 'var(--log-bn, var(--q-cat-gold))' },
  { key: 'blue', label: 'Blue', css: 'var(--q-cat-blue)' },
  { key: 'cyan', label: 'Cyan', css: 'var(--q-cat-cyan)' },
  { key: 'teal', label: 'Teal', css: 'var(--q-cat-teal)' },
  { key: 'green', label: 'Green', css: 'var(--q-cat-green)' },
  { key: 'lime', label: 'Lime', css: 'var(--q-cat-lime)' },
  { key: 'gold', label: 'Gold', css: 'var(--q-cat-gold)' },
  { key: 'amber', label: 'Amber', css: 'var(--q-cat-amber)' },
  { key: 'orange', label: 'Orange', css: 'var(--q-cat-orange)' },
  { key: 'coral', label: 'Coral', css: 'var(--q-cat-coral)' },
  { key: 'red', label: 'Red', css: 'var(--q-cat-red)' },
  { key: 'rose', label: 'Rose', css: 'var(--q-cat-rose)' },
  { key: 'pink', label: 'Pink', css: 'var(--q-cat-pink)' },
  { key: 'magenta', label: 'Magenta', css: 'var(--q-cat-magenta)' },
  { key: 'indigo', label: 'Indigo', css: 'var(--q-cat-indigo)' },
  { key: 'brown', label: 'Brown', css: 'var(--q-cat-brown)' },
  { key: 'slate', label: 'Slate', css: 'var(--q-cat-slate)' },
  { key: 'grey', label: 'Grey', css: 'var(--q-cat-grey)' },
];

/** The CSS for an ink key; null (no ink chosen) = the neutral line ink. */
export function inkCss(key: string | null | undefined): string {
  return GEO_INKS.find((i) => i.key === key)?.css ?? 'var(--text-muted)';
}

export type DetectorId =
  | 'manual'
  | 'cross_border'
  | 'supplier'
  | 'agent'
  | 'airports'
  | 'cities'
  | 'rules'
  | 'default';

export interface DetectorDef {
  id: DetectorId;
  label: string;
  reads: string;
  /** Manual and Default cannot move or be switched off. */
  fixed?: 'first' | 'last';
}

export const DETECTORS: readonly DetectorDef[] = [
  { id: 'manual', label: 'Manual', reads: 'A sector set by hand on the line is kept; nothing overrides it.', fixed: 'first' },
  { id: 'cross_border', label: 'Cross-border', reads: 'Pick-up in one destination and drop-off in another gives the cross-border sector (MY-SG).' },
  { id: 'supplier', label: "Supplier's sector", reads: "The sector on the supplier's card in the Suppliers directory, matched by name or alternate name." },
  { id: 'agent', label: "Agent's sector", reads: "The agent's default sector from the Agents directory." },
  { id: 'airports', label: 'Airports & arrival points', reads: "Pick-up or drop-off matched against each sector's airports and the arrival points' regions." },
  { id: 'cities', label: 'Cities & areas', reads: "The guest catalogue's cities and their areas, as whole words (Batu Ferringhi means Penang)." },
  { id: 'rules', label: 'Custom rules', reads: 'Your own rules, below, in their order.' },
  { id: 'default', label: "Destination's default", reads: "Nothing matched: the line takes its destination's default sector (MY gives KUL).", fixed: 'last' },
];

export interface RuleFieldDef {
  id: string;
  label: string;
  group: 'Service line' | 'Related';
}

export const RULE_FIELDS: readonly RuleFieldDef[] = [
  { id: 'log', label: 'Log', group: 'Service line' },
  { id: 'agent', label: 'Agent', group: 'Service line' },
  { id: 'supplier', label: 'Supplier', group: 'Service line' },
  { id: 'product', label: 'Product', group: 'Service line' },
  { id: 'pickup_location', label: 'Pick-up', group: 'Service line' },
  { id: 'dropoff_location', label: 'Drop-off', group: 'Service line' },
  { id: 'flight', label: 'Flight', group: 'Service line' },
  { id: 'ops_remarks', label: 'Ops remarks', group: 'Service line' },
  { id: 'cs_remarks', label: 'CS remarks', group: 'Service line' },
  { id: 'contact_details', label: 'Contact details', group: 'Service line' },
  { id: 'file_manager', label: 'File manager', group: 'Service line' },
  { id: 'agent.market', label: "Agent's market", group: 'Related' },
  { id: 'agent.region', label: "Agent's region", group: 'Related' },
  { id: 'supplier.city', label: "Supplier's city", group: 'Related' },
  { id: 'supplier.sector', label: "Supplier's sector", group: 'Related' },
  { id: 'flight.airport', label: "Flight's airport", group: 'Related' },
];

export interface RuleOpDef {
  id: string;
  label: string;
  takes: 'value' | 'words' | 'none';
  hint?: string;
}

export const RULE_OPS: readonly RuleOpDef[] = [
  { id: 'contains', label: 'contains', takes: 'value', hint: 'Case does not matter.' },
  { id: 'ilike', label: 'matches the ILIKE pattern', takes: 'value', hint: 'Write the % and _ yourself: %genting%.' },
  { id: 'starts_with', label: 'starts with', takes: 'value' },
  { id: 'equals', label: 'is exactly', takes: 'value', hint: 'Case does not matter.' },
  { id: 'words', label: 'has any of these words', takes: 'words', hint: 'Whole words, case does not matter.' },
  { id: 'regex', label: 'matches the regex', takes: 'value', hint: 'Case-insensitive; checked when you save.' },
  { id: 'empty', label: 'is empty', takes: 'none' },
  { id: 'not_empty', label: 'is not empty', takes: 'none' },
];

export interface SectorRuleCondition {
  field: string;
  op: string;
  value?: string | null;
  values?: string[] | null;
  not?: boolean | null;
}

/** One condition in words: "Pick-up contains Genting". */
export function describeCondition(c: SectorRuleCondition): string {
  const field = RULE_FIELDS.find((f) => f.id === c.field)?.label ?? c.field;
  const op = RULE_OPS.find((o) => o.id === c.op);
  const verb = op?.label ?? c.op;
  const neg = c.not ? 'NOT ' : '';
  if (!op || op.takes === 'none') return `${field} ${neg}${verb}`;
  const what = op.takes === 'words' ? (c.values ?? []).join(', ') : (c.value ?? '');
  return `${field} ${neg}${verb} ${what}`.trim();
}

// ── Reading codes ───────────────────────────────────────────────────────

export interface Geo {
  snapshot: GeoSnapshot;
  /** Active destinations, in order. */
  destinations: GeoDestination[];
  /** Active sectors, in order. */
  sectors: GeoSectorPublic[];
  /** A code or an alias (SIN, bki) to its sector; undefined when unknown. */
  sector(code: string | null | undefined): GeoSectorPublic | undefined;
  /** A destination by code (case-insensitive). */
  destination(code: string | null | undefined): GeoDestination | undefined;
  /** A destination's sectors, in order. */
  sectorsOf(destination: string): GeoSectorPublic[];
}

const norm = (c: string | null | undefined) => (c ?? '').trim().toUpperCase();

export function makeGeo(snapshot: GeoSnapshot): Geo {
  const destinations = [...snapshot.destinations]
    .filter((d) => d.is_active !== false)
    .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const sectors = [...snapshot.sectors].sort((a, b) => a.sort_order - b.sort_order);
  const byCode = new Map<string, GeoSectorPublic>();
  for (const s of sectors) byCode.set(norm(s.code), s);
  for (const s of sectors) for (const a of s.aliases ?? []) if (!byCode.has(norm(a))) byCode.set(norm(a), s);
  return {
    snapshot,
    destinations,
    sectors,
    sector: (code) => byCode.get(norm(code)),
    destination: (code) => destinations.find((d) => d.code === norm(code)),
    sectorsOf: (destination) => sectors.filter((s) => s.destination === norm(destination)),
  };
}

// ── Why a line has its sector (the database's sector engine, G2) ───────

/**
 * A line's `sector_source` in words, as the Logs page's hover says it:
 * "supplier's sector (FTZ)", "area Batu Ferringhi", "custom rule 12". The
 * API's `SectorEngine.Describe` says the same; both are pinned by tests.
 */
export function describeSectorSource(source: string | null | undefined): string {
  const s = (source ?? '').trim();
  if (!s) return 'set before sector detection';
  const i = s.indexOf(':');
  const kind = i < 0 ? s : s.slice(0, i);
  const arg = i < 0 ? '' : s.slice(i + 1);
  switch (kind) {
    case 'manual':
      return 'set by hand';
    case 'default':
      return 'destination default';
    case 'supplier':
      return `supplier's sector (${arg})`;
    case 'agent':
      return `agent's sector (${arg})`;
    case 'cross_border':
      return `cross-border (${arg})`;
    case 'airport':
      return `airport ${arg}`;
    case 'arrival':
      return `arrival point ${arg}`;
    case 'city':
      return `city ${arg}`;
    case 'area':
      return `area ${arg}`;
    case 'rule':
      return arg === 'draft' ? 'the rule being edited' : `custom rule ${arg}`;
    case 'error':
      return arg.startsWith('rule:') ? `custom rule ${arg.slice(5)} failed, destination default used` : `detection failed (${arg}), fallback used`;
    default:
      return s;
  }
}

/** The hover on a line's sector: "PEN · supplier's sector (FTZ)"; undefined when the line says nothing. */
export function sectorTitle(sector: string | null | undefined, source: string | null | undefined): string | undefined {
  if (!source || !source.trim() || !sector) return undefined;
  return `${sector.trim().toUpperCase()} · ${describeSectorSource(source)}`;
}
